import React, { useEffect, useMemo, useRef, useState } from "react";

import moment from "moment";
import Timeline from "okast-timeline";
import { useDragLayer } from "react-dnd";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import InterstitialAdbreakModal from "./InterstitialAdbreakModal";
import SelectedMediaSidePanel from "./SelectedMediaSidePanel";
import SelectedTimeslotSidePanel from "./SelectedTimeslotSidePanel";
import { DRAG_TYPE } from "../../../constants/dragTypes";
import useFAST from "../../../contexts/FASTContext";
import useAddScheduleListMediaMutation from "../../../hooks/mutations/useAddScheduleListMediaMutation";
import useReplaceScheduleListMediaMutation from "../../../hooks/mutations/useReplaceScheduleListMediaMutation";
import useUpdateScheduleListMediaMutation from "../../../hooks/mutations/useUpdateScheduleListMediaMutation";
import useFASTScheduleWebSocket from "../../../hooks/useFASTScheduleWebSocket";
import useKeyPress from "../../../hooks/useKeyPress";
import useTimebounds from "../../../hooks/useTimebounds";
import useTimelineItems from "../../../hooks/useTimelineItems";
import {
  getDropzoneTimelineAcceptedFormats,
  getSchedulingDropZoneItems,
  getSchedulingTimeslotItems,
} from "../../../utils/fast";
import GroupRenderer from "../timeline/GroupRenderer";
import ItemRenderer from "../timeline/ItemRenderer";
import SchedulingTimelineLoader from "../timeline/SchedulingTimelineLoader";
import {
  MAX_ZOOM_OUT_DURATION,
  MIN_ZOOM_IN_DURATION,
  TimelineRenderTypes,
} from "../timeline/timeline";
import TimelineFillerTooltip from "../timeline/TimelineFillerTooltip";
import TimelineHeader, { SIDEBAR_SIZE } from "../timeline/TimelineHeader";
import TimelineMarker from "../timeline/TimelineMarker";

import type {
  OnItemDragObjectMove,
  OnItemDragObjectResize,
  TimelineProps,
} from "okast-timeline";
import type { Component, JSX, RefObject } from "react";
import type { DragLayerMonitor } from "react-dnd";
import type { TooltipRefProps } from "react-tooltip";

import type { MediaFormat } from "../../../constants/media";
import type { KeyWithTranslation, MediaLibraryDragItem } from "../../../types";
import type { OnDropItemParams } from "../timeline/ItemRenderer";
import type {
  AllSchedulingItems,
  GroupTimeline,
  SchedulingTimelineItem,
  TimelineRef,
} from "../timeline/timeline.types";

interface SchedulingTimelineProps {
  isLoading: boolean;
  mediaFormatList?: KeyWithTranslation<MediaFormat>[];
  previewMediaTooltipRef: RefObject<TooltipRefProps | null>;
  isEditMode: boolean;
  isUpdateDisabled: boolean;
}

function SchedulingTimeline({
  isLoading,
  mediaFormatList,
  previewMediaTooltipRef,
  isEditMode,
  isUpdateDisabled,
}: SchedulingTimelineProps): JSX.Element {
  const { dispatch, state } = useFAST();
  const isShiftKeyPress = useKeyPress("Shift");
  const { timelineStartDate, timelineEndDate, updateTimebounds } =
    useTimebounds();
  const { getCanUpdateSchedule } = useFASTScheduleWebSocket();

  const { t } = useTranslation("editor-fast-channel");

  const [adbreakModalParams, setAdbreakModalParams] = useState<{
    isOpen: boolean;
    media: MediaLibraryDragItem | null;
    startTime: number | null;
    scheduleId: string | null;
    maxEndTime: number | null;
  }>({
    isOpen: false,
    media: null,
    startTime: null,
    scheduleId: null,
    maxEndTime: null,
  });
  const [timelineItems, setTimelineItems] = useState<SchedulingTimelineItem[]>(
    [],
  );
  const { schedules, timelineScheduleSelected } = state;

  const { currentTimeslots, currentTimelineMediaList, timelineContent } =
    useTimelineItems();

  const updateMediaMutation = useUpdateScheduleListMediaMutation();

  const { isLoading: isLoadingUpdateMedia, variables: variablesUpdateMedia } =
    updateMediaMutation;

  const addMediaMutation = useAddScheduleListMediaMutation();

  const { isLoading: isLoadingAddMedia, context: contextAddMedia } =
    addMediaMutation;

  const fillerTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const ref = useRef<
    | (Component<TimelineProps<AllSchedulingItems, GroupTimeline>> &
        TimelineRef)
    | null
  >(null);

  const setFillerTimeoutRef = (timeout: NodeJS.Timeout): void => {
    fillerTimeoutRef.current = timeout;
  };

  const dropzoneAcceptedFormats =
    getDropzoneTimelineAcceptedFormats(mediaFormatList);

  const groups = useMemo(
    () =>
      schedules.map((schedule) => ({
        scheduleId: schedule.uuid,
        title: schedule.name,
        color: schedule.color,
        isActive: schedule.uuid === timelineScheduleSelected,
        id: schedule.uuid,
        framerate: schedule.framerate,
        broadcasts: schedule.broadcasts,
        isBaseline: schedule.default,
      })),
    [schedules, timelineScheduleSelected],
  );

  const { isDragging } = useDragLayer<{
    isDragging: boolean;
  }>((monitor: DragLayerMonitor<MediaLibraryDragItem | null>) => ({
    isDragging:
      monitor.isDragging() &&
      typeof monitor.getItemType() === "string" &&
      (monitor.getItemType() as string).includes(DRAG_TYPE.MEDIA_LIBRARY_ITEM),
  }));

  const setActiveGroup = (scheduleId: string): void => {
    dispatch({
      type: "SET_ACTIVE_SCHEDULE",
      id: scheduleId,
    });
  };

  const dropZoneItems = useMemo(() => {
    if (!currentTimeslots) return [];
    return getSchedulingDropZoneItems(
      currentTimeslots.timeslot,
      currentTimeslots.variantTimeslots,
    );
  }, [currentTimeslots]);

  useEffect(() => {
    const updatedTimelineItems = currentTimelineMediaList.map((currentItem) => {
      const hasVariantMedia = currentItem.variantMediaList.length > 0;

      const itemAddInProgress =
        isLoadingAddMedia &&
        contextAddMedia?.newScheduleItem?.uuid === currentItem.uuid;

      const itemUpdateInProgress =
        isLoadingUpdateMedia && variablesUpdateMedia?.uuid === currentItem.uuid;

      const isLoadingItem = itemUpdateInProgress || itemAddInProgress;

      const itemStartTime = itemUpdateInProgress
        ? variablesUpdateMedia?.startTime
        : currentItem.startTime;

      const itemEndTime = itemUpdateInProgress
        ? variablesUpdateMedia?.endTime
        : currentItem.endTime;

      const canMove =
        !isUpdateDisabled &&
        isEditMode &&
        moment(currentItem?.startTime).isAfter(moment()) &&
        currentItem.isOnBaseline &&
        !hasVariantMedia &&
        !isLoadingItem;

      return {
        id: currentItem.uuid,
        group: currentItem.scheduleId,
        title: currentItem?.label,
        start_time: moment(itemStartTime),
        end_time: moment(itemEndTime),
        canMove,
        duration: currentItem.duration,
        format: currentItem.format,
        isFirstVariantMedia: currentItem.isFirstVariantMedia,
        isOnBaseline: currentItem.isOnBaseline,
        variantMediaList: currentItem.variantMediaList,
        type: currentItem.type,
        canSelect: !isLoadingItem,
        scheduleId: currentItem.scheduleId,
        nextMediaId: currentItem.nextMediaId,
        timeslot: currentItem.timeslot,
        isLoading: isLoadingItem,
        interstitialAdbreaks: currentItem.interstitialAdbreaks,
        poster: currentItem.poster,
      };
    });

    setTimelineItems(updatedTimelineItems);
  }, [
    contextAddMedia?.newScheduleItem?.uuid,
    currentTimelineMediaList,
    isEditMode,
    isLoadingAddMedia,
    isLoadingUpdateMedia,
    variablesUpdateMedia,
    isUpdateDisabled,
  ]);

  const timelineTimeslotItems = useMemo(
    () => getSchedulingTimeslotItems(currentTimelineMediaList),
    [currentTimelineMediaList],
  );

  const handleChangeTime = (
    visibleTimeStart: number,
    visibleTimeEnd: number,
  ): void => {
    if (isDragging) {
      return;
    }
    const roundedTimeStart = Math.round(visibleTimeStart);
    const roundedTimeEnd = Math.round(visibleTimeEnd);

    updateTimebounds({
      startTime: roundedTimeStart,
      endTime: roundedTimeEnd,
      isDebounced: true,
    });
  };

  const moveResizeValidator = (
    _: "move" | "resize",
    item: SchedulingTimelineItem,
    time: number,
  ): number => {
    if (item.isOnBaseline) {
      const itemEndTime = time + item.duration;

      const availableStartDate =
        currentTimeslots?.timeslot.getAvailableStartDate({
          start: time,
          end: itemEndTime,
          id: item.id,
        });

      return availableStartDate ?? item.start_time.valueOf();
    }

    const variant = currentTimeslots?.variantTimeslots.find(
      (variantTimeslot) => variantTimeslot.id === item.scheduleId,
    );

    if (variant) {
      const currentSlot = variant.getUsedSlotById(item.id);

      if (currentSlot) {
        const availableStartDate = variant.getAvailableStartDate({
          start: time,
          end: moment(time).add(item.duration, "milliseconds").valueOf(),
          id: currentSlot.id,
          parentId: currentSlot.parentId,
        });
        return availableStartDate ?? item.start_time.valueOf();
      }
    }

    return item.start_time.valueOf();
  };

  const handleItemMove = (itemId: string, dragTime: number): void => {
    const item = timelineContent[itemId];

    if (!item) return;

    if (moment(item.initialStartTime).valueOf() !== dragTime) {
      getCanUpdateSchedule({
        onSuccess: () =>
          updateMediaMutation.mutate({
            uuid: item.uuid,
            startTime: moment(dragTime).toISOString(),
            endTime: moment(dragTime).add(item.duration).toISOString(),
          }),
        onError: () => {
          toast.error(t("programUpdateErrorNoReason"));
        },
      });
    }
  };

  const handleItemClick = (itemId: string): void => {
    dispatch({
      type: "SET_ITEM_SELECTED",
      id: itemId,
    });
  };

  const handleItemDrag = (
    itemDragObject: OnItemDragObjectMove | OnItemDragObjectResize,
  ): void => {
    const { itemId, time } = itemDragObject;

    const updatedList = timelineItems.map((media) => {
      if (media.id === itemId) {
        return {
          ...media,
          start_time: moment(time),
          end_time: moment(time + media.duration),
        };
      }
      return media;
    });

    setTimelineItems(updatedList);
  };

  const addMedia = ({
    item,
    startTime,
    scheduleId,
    maxEndTime,
  }: OnDropItemParams): void => {
    dispatch({
      type: "SET_ACTIVE_DROP_ZONE",
      dropZoneId: null,
    });

    if (!scheduleId) return;

    if (startTime < timelineStartDate) {
      const range = timelineEndDate - timelineStartDate;

      updateTimebounds({
        startTime,
        endTime: startTime + range,
      });
    }

    if (item.cuepoints?.length) {
      setAdbreakModalParams({
        isOpen: true,
        media: item,
        scheduleId,
        startTime,
        maxEndTime,
      });
    } else {
      getCanUpdateSchedule({
        onSuccess: () =>
          addMediaMutation.mutate({
            mediaToAdd: item,
            startTime,
            scheduleId,
          }),
        onError: () => {
          toast.error(t("programAddError"));
        },
      });
    }
  };

  const replaceMediaMutation = useReplaceScheduleListMediaMutation();

  const replaceMedia = (
    itemToDelete: SchedulingTimelineItem,
    itemToAdd: MediaLibraryDragItem,
  ): void => {
    if (itemToAdd?.mediaId && itemToDelete.scheduleId) {
      const startDate = itemToDelete.start_time.valueOf();

      getCanUpdateSchedule({
        onSuccess: () =>
          replaceMediaMutation.mutate({
            mediaUuidToDelete: itemToDelete.id,
            mediaToAdd: {
              mediaUuid: itemToAdd.mediaId,
              startDate,
            },
            scheduleId: itemToDelete.scheduleId,
          }),
        onError: () => {
          toast.error(t("programReplaceError"));
        },
      });
    }
  };

  const handleModalClose = (): void => {
    setAdbreakModalParams({
      isOpen: false,
      media: null,
      scheduleId: null,
      maxEndTime: null,
      startTime: null,
    });
  };

  const getLineClassNamesForGroup = (group: GroupTimeline): string[] =>
    group.isBaseline ? ["baseline"] : [];

  const handleCanvasClick = (
    groupId: string,
    timestamp: number,
    domEvent: React.SyntheticEvent,
  ): void => {
    const defaultScheduleId = schedules.find(
      (schedule) => schedule.default,
    )?.uuid;

    if (groupId !== defaultScheduleId) return;

    const { x, y } = domEvent.nativeEvent as MouseEvent;

    clearTimeout(fillerTimeoutRef.current);

    dispatch({
      type: "SET_FILLER_TOOLTIP",
      timestamp,
      clickPosition: { x, y },
    });
  };

  return (
    <div className="bg-card relative block h-[calc(100vh-350px)] w-full overflow-x-hidden rounded-[0.3125rem] drop-shadow select-none">
      <SchedulingTimelineLoader
        isLoading={isLoading}
        isUpdateDisabled={isUpdateDisabled}
      />
      <Timeline
        ref={ref}
        withoutZoomButtons
        buffer={1}
        canChangeGroup={false}
        canResize={false}
        canWheelZoom={false}
        dragSnap={0.5 * 60 * 1000}
        groups={groups}
        items={[...timelineItems, ...dropZoneItems, ...timelineTimeslotItems]}
        itemSizeRatio={0.9}
        lineClassNamesForGroup={getLineClassNamesForGroup}
        maxLineSize={300}
        maxZoomOutDuration={MAX_ZOOM_OUT_DURATION}
        minLineSize={150}
        minZoomInDuration={MIN_ZOOM_IN_DURATION}
        moveResizeValidator={moveResizeValidator}
        onCanvasClick={handleCanvasClick}
        onItemClick={handleItemClick}
        onItemDrag={handleItemDrag}
        onItemMove={handleItemMove}
        onTimeChange={handleChangeTime}
        sidebarSize={SIDEBAR_SIZE}
        visibleTimeEnd={timelineEndDate}
        visibleTimeStart={timelineStartDate}
        groupRenderer={({ group }): JSX.Element => (
          <GroupRenderer
            group={group}
            handleClick={setActiveGroup}
            timelineRef={ref}
          />
        )}
        itemRenderer={({ itemContext, item, getItemProps }): JSX.Element => (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          <ItemRenderer
            dropzoneAcceptedFormats={dropzoneAcceptedFormats}
            getItemProps={getItemProps}
            isReplaceMode={isShiftKeyPress}
            item={item}
            itemContext={itemContext}
            onDropItem={addMedia}
            onReplaceItem={replaceMedia}
            previewMediaTooltipRef={previewMediaTooltipRef}
            renderType={TimelineRenderTypes.SCHEDULING}
            timelineRef={ref}
          />
        )}
      >
        <TimelineHeader timelineRef={ref} />
        <TimelineMarker />
        <TimelineFillerTooltip setFillerTimeoutRef={setFillerTimeoutRef} />
      </Timeline>
      <InterstitialAdbreakModal
        adbreakModalParams={adbreakModalParams}
        addMediaMutation={addMediaMutation}
        onClose={handleModalClose}
      />
      <SelectedMediaSidePanel
        isEditMode={isEditMode}
        isUpdateDisabled={isUpdateDisabled}
      />
      <SelectedTimeslotSidePanel
        isEditMode={isEditMode}
        isUpdateDisabled={isUpdateDisabled}
        timelineTimeslotItems={timelineTimeslotItems}
      />
    </div>
  );
}

export default SchedulingTimeline;
